import React, { useCallback, useEffect, useState } from "react";

import EmptyState from "../../components/EmptyState";
import PageHead from "../../components/PageHead";
import * as api from "./api";
import OfferPicker from "./OfferPicker";
import { Panel } from "./panels";

/**
 * Workstation · Research — the decision brief.
 *
 * REBUILT 2026-09-09 (RESEARCH-DECISION-BRIEF-BUILD, Founder ruling).
 * ==================================================================
 * The previous version was a source-by-source report: fourteen panels, each
 * summarising one source, with the raw scrape underneath. The Founder's verdict
 * was that most of it was noise — not because the gathering was wrong, but
 * because a list of what each source returned is not a decision.
 *
 * This page now opens with the answer to one question: **who is the buyer, what
 * hurts, what do they want, what stops them, and what should we test?**
 * The sources, their failures and the raw evidence move into a collapsed
 * coverage drawer — available, checkable, and not on the main canvas.
 *
 * THE ORDER IS THE ARGUMENT
 * =========================
 * Market truth → pain → objections → language → angles → contradictions → risk.
 * That is the decision chain, and each section is composed from typed evidence
 * units ranked across ALL sources together, so a claim cites an ITEM rather than
 * a source name.
 *
 * WHAT STAYS VISIBLE NO MATTER WHAT
 * =================================
 * `zero_research` is a first-class state (`WORKSTATION_RESEARCH.md` §3): an offer
 * whose sources obtained no items is labelled as such before any prose. Absence
 * of an attempt is never rendered as absence of a finding.
 */

const PAIN_LABEL = {
  functional: "Functional pain",
  emotional: "Emotional pain",
  identity_status: "Identity / status pain",
  fear_of_inaction: "Fear of inaction",
  failed_alternatives: "Failed alternatives",
  desired_transformation: "Desired transformation",
};

const LANG_LABEL = {
  hooks: "Hooks",
  problem_descriptions: "Problem descriptions",
  desired_outcomes: "Desired outcomes",
  objections: "Objections",
  comparisons_metaphors: "Comparisons & metaphors",
};

const IMPACT_ORDER = { high: 0, medium: 1, low: 2 };
const STRENGTH_ORDER = { strong: 0, moderate: 1, exploratory: 2 };

function when(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return String(ts);
  }
}

/** A citation that resolves. Hovering shows the evidence it rests on. */
function Refs({ refs, evidence }) {
  if (!refs || !refs.length) return null;
  const title = refs
    .map((r) => (evidence || []).find((e) => e.i === r))
    .filter(Boolean)
    .map((e) => `[${e.i}] (${e.source}) ${String(e.text || "").slice(0, 160)}`)
    .join("\n\n");
  return (
    <span className="dim" style={{ marginLeft: 6, fontSize: "var(--f-label, 10.5px)" }} title={title}>
      [{refs.join(",")}]
    </span>
  );
}

function Section({ title, subtitle, children, dominant }) {
  return (
    <Panel title={title} subtitle={subtitle} dominant={dominant}>
      {children}
    </Panel>
  );
}

function MarketTruth({ items, evidence }) {
  if (!items || !items.length) return null;
  return (
    <Section title="Market truth" subtitle="The conclusions that should drive the decision." dominant>
      <ol className="ws-list">
        {items.map((m, i) => (
          <li key={i} className="ws-understanding-item" style={{ display: "block" }}>
            {m.conclusion}
            <Refs refs={m.refs} evidence={evidence} />
          </li>
        ))}
      </ol>
    </Section>
  );
}

function PainMap({ map, evidence }) {
  const entries = Object.entries(map || {});
  if (!entries.length) return null;
  const filled = entries.filter(([, v]) => (v || []).length);
  return (
    <Section
      title="Audience pain map"
      subtitle={`${filled.length} of ${entries.length} categories have evidence behind them.`}
    >
      {entries.map(([key, list]) => (
        <div key={key} style={{ marginBottom: 10 }}>
          <div className="ws-kpi-label">{PAIN_LABEL[key] || key}</div>
          {(list || []).length ? (
            <ul className="ws-list">
              {list.map((p, i) => (
                <li key={i} className="ws-understanding-item" style={{ display: "block" }}>
                  {p.pain}
                  <Refs refs={p.refs} evidence={evidence} />
                </li>
              ))}
            </ul>
          ) : (
            /* An empty category is a fact about the evidence, not a gap to fill
               with inference. */
            <div className="dim" style={{ fontSize: "var(--f-label, 10.5px)" }}>
              Nothing in the evidence supports this category.
            </div>
          )}
        </div>
      ))}
    </Section>
  );
}

function Objections({ items, evidence }) {
  if (!items || !items.length) return null;
  const sorted = [...items].sort(
    (a, b) => (IMPACT_ORDER[a.impact] ?? 3) - (IMPACT_ORDER[b.impact] ?? 3)
  );
  return (
    <Section title="Objection hierarchy" subtitle="Ranked by likely impact on conversion.">
      <ol className="ws-list">
        {sorted.map((o, i) => (
          <li key={i} className="ws-understanding-item" style={{ display: "block", marginBottom: 10 }}>
            <span className="ws-tag" data-impact={o.impact}>{o.impact || "?"}</span>{" "}
            <strong>{o.objection}</strong>
            <Refs refs={o.refs} evidence={evidence} />
            {o.why_it_blocks ? <div className="dim">{o.why_it_blocks}</div> : null}
            {(o.customer_language || []).length ? (
              <ul className="ws-list" style={{ marginTop: 4 }}>
                {o.customer_language.map((q, j) => (
                  <li key={j} className="dim">“{q}”</li>
                ))}
              </ul>
            ) : null}
          </li>
        ))}
      </ol>
    </Section>
  );
}

function LanguageBank({ bank }) {
  const entries = Object.entries(bank || {}).filter(([, v]) => (v || []).length);
  if (!entries.length) return null;
  const total = entries.reduce((n, [, v]) => n + v.length, 0);
  return (
    <Section
      title="Audience language bank"
      subtitle={`${total} verbatim phrases. Anything that did not match its source exactly was dropped.`}
    >
      {entries.map(([key, list]) => (
        <div key={key} style={{ marginBottom: 10 }}>
          <div className="ws-kpi-label">{LANG_LABEL[key] || key}</div>
          <ul className="ws-list">
            {list.slice(0, 12).map((p, i) => (
              <li key={i} className="ws-understanding-item" style={{ display: "block" }}>
                “{p.phrase}”
                <span className="dim" style={{ marginLeft: 6, fontSize: "var(--f-label, 10.5px)" }}>
                  — {p.source}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </Section>
  );
}

function Angles({ angles, insufficient, evidence }) {
  return (
    <Section
      title="Angle opportunities"
      subtitle={angles && angles.length ? `${angles.length} to test, strongest evidence first.` : undefined}
      dominant
    >
      {!angles || !angles.length ? (
        /* Required structure is not required quantity. A thin offer gets an
           honest admission, never five padded angles. */
        <div className="state ws-state" role="status">
          <strong>Not enough evidence for a confident angle.</strong>
          <div style={{ marginTop: 4 }}>
            {insufficient || "The research did not support a specific creative premise."}
          </div>
        </div>
      ) : (
        [...angles]
          .sort((a, b) => (STRENGTH_ORDER[a.evidence_strength] ?? 3) - (STRENGTH_ORDER[b.evidence_strength] ?? 3))
          .map((a, i) => (
            <div key={i} className="ws-understanding-item" style={{ display: "block", marginBottom: 14 }}>
              <div>
                <span className="ws-tag" data-strength={a.evidence_strength}>
                  {a.evidence_strength || "?"}
                </span>{" "}
                <strong>{a.angle}</strong>
                <Refs refs={a.refs} evidence={evidence} />
              </div>
              <dl style={{ margin: "6px 0 0" }}>
                {[
                  ["Audience", a.audience_segment],
                  ["Core tension", a.core_tension],
                  ["Promise", a.promise],
                  ["Why it may work", a.why_it_may_work],
                  ["Objection to overcome", a.objection_to_overcome],
                  ["Recommended test", a.recommended_test],
                ].map(([label, value]) =>
                  value ? (
                    <div key={label} style={{ marginBottom: 3 }}>
                      <span className="ws-kpi-label">{label}: </span>
                      <span>{value}</span>
                    </div>
                  ) : null
                )}
              </dl>
              {(a.supporting_language || []).length ? (
                <div className="dim" style={{ marginTop: 4 }}>
                  Supporting language: {a.supporting_language.map((q) => `“${q}”`).join(" · ")}
                </div>
              ) : null}
            </div>
          ))
      )}
    </Section>
  );
}

function Contradictions({ data, evidence }) {
  const d = data || {};
  const any =
    (d.disagreements || []).length ||
    (d.could_not_validate || []).length ||
    (d.what_would_help_most || []).length;
  if (!any) return null;
  return (
    <Section title="Contradictions and blind spots" subtitle="What disagrees, what could not be checked, and what would help most.">
      {(d.disagreements || []).length ? (
        <>
          <div className="ws-kpi-label">Sources disagree</div>
          <ul className="ws-list">
            {d.disagreements.map((x, i) => (
              <li key={i} className="ws-understanding-item" style={{ display: "block" }}>
                <strong>{x.between}</strong> — {x.what}
                <Refs refs={x.refs} evidence={evidence} />
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {(d.could_not_validate || []).length ? (
        <>
          <div className="ws-kpi-label" style={{ marginTop: 8 }}>Could not be validated</div>
          <ul className="ws-list">
            {d.could_not_validate.map((x, i) => <li key={i} className="dim">{x}</li>)}
          </ul>
        </>
      ) : null}
      {(d.what_would_help_most || []).length ? (
        <>
          <div className="ws-kpi-label" style={{ marginTop: 8 }}>What would most improve this decision</div>
          <ul className="ws-list">
            {d.what_would_help_most.map((x, i) => <li key={i} className="dim">{x}</li>)}
          </ul>
        </>
      ) : null}
    </Section>
  );
}

function BusinessRisk({ risk, missing, evidence }) {
  if (!risk) {
    return missing ? (
      <Section title="Business risk">
        <div className="state ws-state" role="status">
          No business-risk assessment was returned. That is a gap in the report,
          not a finding that there is no risk.
        </div>
      </Section>
    ) : null;
  }
  const concerns = [...(risk.concerns || [])].sort(
    (a, b) => (IMPACT_ORDER[a.severity] ?? 3) - (IMPACT_ORDER[b.severity] ?? 3)
  );
  return (
    <Section
      title="Business risk"
      subtitle={risk.run_recommendation ? risk.run_recommendation.replace(/_/g, " ") : undefined}
    >
      <p style={{ marginTop: 0 }}>{risk.assessment}</p>
      {risk.recommendation_reason ? <p className="dim">{risk.recommendation_reason}</p> : null}
      {concerns.length ? (
        <ul className="ws-list">
          {concerns.map((c, i) => (
            <li key={i} className="ws-understanding-item" style={{ display: "block" }}>
              <span className="ws-tag" data-severity={c.severity}>{c.severity || "?"}</span>{" "}
              {c.risk}
              <Refs refs={c.refs} evidence={evidence} />
              {c.mitigation ? <div className="dim">Mitigation: {c.mitigation}</div> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </Section>
  );
}

/** Sources, failures, timestamps and raw evidence — available, not on the canvas. */
function CoverageDrawer({ brief, records }) {
  const [open, setOpen] = useState(false);
  const cov = (brief && brief.coverage) || {};
  const evidence = (brief && brief.evidence) || [];
  const counts = ["fetched", "empty", "failed", "not_configured", "unregistered"]
    .map((k) => [k, (cov[k] || []).length]);

  return (
    <Panel title="Research coverage" subtitle="Which sources were consulted, what they returned, and the evidence itself.">
      <button type="button" className="btn" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        {open ? "Hide research coverage" : "Show research coverage"}
      </button>
      <div className="dim" style={{ marginTop: 6, fontSize: "var(--f-label, 10.5px)" }}>
        {counts.map(([k, n]) => `${n} ${k.replace(/_/g, " ")}`).join(" · ")}
        {evidence.length ? ` · ${evidence.length} evidence units used` : ""}
      </div>

      {open ? (
        <div style={{ marginTop: 10 }}>
          {counts.map(([key, n]) =>
            n ? (
              <div key={key} style={{ marginBottom: 8 }}>
                <div className="ws-kpi-label">{key.replace(/_/g, " ")}</div>
                <ul className="ws-list">
                  {(cov[key] || []).map((s, i) => (
                    <li key={i} className="ws-understanding-item" style={{ display: "block" }}>
                      {s.source}
                      {s.items ? <span className="dim"> · {s.items} item(s)</span> : null}
                      {s.reason ? <div className="dim">{s.reason}</div> : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null
          )}

          {evidence.length ? (
            <>
              <div className="ws-kpi-label" style={{ marginTop: 10 }}>Evidence used, in rank order</div>
              <ul className="ws-list">
                {evidence.slice(0, 40).map((e) => (
                  <li key={e.i} className="ws-understanding-item" style={{ display: "block" }}>
                    <span className="dim">[{e.i}]</span> <span className="ws-tag">{e.kind}</span>{" "}
                    {e.url ? (
                      <a href={e.url} target="_blank" rel="noopener noreferrer">{e.source}</a>
                    ) : (
                      <span className="dim">{e.source}</span>
                    )}
                    {e.verbatim ? <span className="ws-tag">verbatim</span> : null}
                    <div className="dim">{e.text}</div>
                  </li>
                ))}
              </ul>
            </>
          ) : null}

          {records && records.length ? (
            <div className="dim" style={{ marginTop: 8, fontSize: "var(--f-label, 10.5px)" }}>
              Last gathered: {when(records[0] && records[0].generated_at)}
            </div>
          ) : null}
        </div>
      ) : null}
    </Panel>
  );
}

function Brief({ offer }) {
  const offerId = offer && offer.offer_id;
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [runError, setRunError] = useState(null);
  const [note, setNote] = useState(null);

  const load = useCallback(async () => {
    if (!offerId) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    const res = await api.getOfferResearch(offerId);
    setState({
      loading: false,
      data: res.ok ? res.data : null,
      error: res.ok ? null : res.error || "request failed",
    });
  }, [offerId]);

  useEffect(() => {
    load();
  }, [load]);

  /* An honest progress indicator. The previous button said "about a minute"
     while a real run took six and a half. A wrong estimate is worse than none. */
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [running]);

  const runPack = async () => {
    setRunning(true);
    setElapsed(0);
    setRunError(null);
    const res = await api.runOfferResearchPack(offerId, {});
    if (!res.ok) setRunError(res.error || "request failed");
    else {
      setNote((res.data && res.data.synthesis) || null);
      await load();
    }
    setRunning(false);
  };

  if (state.loading) return <div className="state ws-state" role="status">Loading research…</div>;
  if (state.error) {
    return <div className="state ws-state" role="alert">Could not load research: {state.error}</div>;
  }

  const data = state.data || {};
  const brief = data.brief;
  const records = data.records || [];
  const evidence = (brief && brief.evidence) || [];

  const mins = Math.floor(elapsed / 60);
  const secs = String(elapsed % 60).padStart(2, "0");

  return (
    <>
      <div className="ws-toolbar">
        <button type="button" className="btn btn-primary" onClick={runPack} disabled={running}>
          {running
            ? `Gathering and analysing… ${mins}:${secs}`
            : data.never_run
            ? "Gather research"
            : "Refresh research"}
        </button>
        {running ? (
          <span className="dim">
            Reads {records.length || "a dozen"} sources, then writes the brief. Usually 5–8 minutes.
          </span>
        ) : brief && brief.ok ? (
          <span className="dim">
            {(brief.units_used || 0)} evidence units used of {(brief.units_extracted || 0)} extracted
            {brief.providers && brief.providers.length ? ` · via ${brief.providers.join(", ")}` : ""}
            {typeof brief.cost_usd === "number" ? ` · $${brief.cost_usd.toFixed(4)}` : ""}
            {` · ${when(data.brief_generated_at)}`}
          </span>
        ) : null}
      </div>

      {runError ? (
        <div className="state ws-state" role="alert" style={{ marginBottom: 12 }}>
          The research run itself failed (no sources were recorded): {runError}
        </div>
      ) : null}

      {data.never_run ? (
        <EmptyState
          title="No research gathered yet"
          detail="Press “Gather research”. Cipher reads the offer's own funnel, searches for what customers say about it, pulls audience context, and checks competitor ad libraries — then writes the decision brief from what it found. It takes several minutes because the sources run one at a time through a single browser."
        />
      ) : !brief ? (
        <EmptyState
          title="Research was gathered, but no brief has been written"
          detail="Press “Refresh research” to analyse what was gathered."
        />
      ) : brief.zero_research ? (
        /* First-class state, per WORKSTATION_RESEARCH.md §3. */
        <div className="ws-analytics-grid">
          <Panel title="No research was obtained" dominant>
            <div className="state ws-state" role="status">{brief.reason}</div>
          </Panel>
          <CoverageDrawer brief={brief} records={records} />
        </div>
      ) : !brief.ok ? (
        <div className="ws-analytics-grid">
          <Panel title="The brief could not be written" dominant>
            <div className="state ws-state" role="status">{brief.reason}</div>
          </Panel>
          <CoverageDrawer brief={brief} records={records} />
        </div>
      ) : (
        <div className="ws-analytics-grid">
          <MarketTruth items={brief.market_truth} evidence={evidence} />
          <PainMap map={brief.pain_map} evidence={evidence} />
          <Objections items={brief.objections} evidence={evidence} />
          <LanguageBank bank={brief.language_bank} />
          <Angles angles={brief.angles} insufficient={brief.insufficient_evidence} evidence={evidence} />
          <Contradictions data={brief.contradictions} evidence={evidence} />
          <BusinessRisk risk={brief.business_risk} missing={brief.business_risk_missing} evidence={evidence} />
          <CoverageDrawer brief={brief} records={records} />
        </div>
      )}
    </>
  );
}

export default function Research({ selection }) {
  const { offers, selected, selectedId, select, error } = selection;

  return (
    <>
      <PageHead
        title="Research"
        subtitle={
          selected
            ? `Who buys ${selected.name || "this offer"}, what hurts, what stops them, and what to test.`
            : "External market intelligence, per offer."
        }
      />

      <OfferPicker offers={offers} selectedId={selectedId} onSelect={select} error={error} />

      {!selectedId ? (
        <div className="state ws-state" role="status">Select an offer to see its research.</div>
      ) : (
        <Brief offer={selected} />
      )}
    </>
  );
}
